import React, { useState, useRef } from "react";
import { Button } from "primereact/button";
import { ProgressBar } from "primereact/progressbar";
import { OverlayPanel } from "primereact/overlaypanel";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { Badge } from "primereact/badge";
import axios from "axios";
import styles from "./BottomDrawer.module.scss";
import { DEV } from "../../../../services/constants";
import DeliveryStopDialog from "../../Dialogs/DeliveryStopDialog";

const STOP_NUMBER_COLORS = ["#6366f1", "#8b5cf6", "#06b6d4", "#10b981", "#f59e0b", "#ef4444"];

const BottomDrawer = ({ deliveryStops, deliveryStatistics, isDesktop, updateStopStatus, toast: parentToast, onCompleteStop, driverLocation, deliveryId, setDeliveryStops }) => {
    const [selectedStopForMenu, setSelectedStopForMenu] = useState(null);
    const [selectedStop, setSelectedStop] = useState(null);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [showMapSelectionDialog, setShowMapSelectionDialog] = useState(false);
    const [stopForDirections, setStopForDirections] = useState(null);

    const overlayRefs = useRef({});
    const toast = useRef(null);

    const handleGetDirections = (stop, event) => {
        event.stopPropagation();

        const destination = `${stop.latitude},${stop.longitude}`;
        const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
        const isAndroid = /Android/.test(navigator.userAgent);

        if (isIOS) {
            setStopForDirections(stop);
            setShowMapSelectionDialog(true);
        } else if (isAndroid) {
            const googleMapsAppUrl = `google.navigation:q=${destination}&mode=d`;
            window.location.href = googleMapsAppUrl;
            const toastRef = toast.current || parentToast?.current;
            if (toastRef) {
                toastRef.show({
                    severity: "success",
                    summary: "Navigation Started",
                    detail: `Navigating to ${stop.clientName}.`,
                    life: 4000,
                    icon: "pi pi-map-marker",
                });
            }
        } else {
            const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${destination}&travelmode=driving`;
            window.open(googleMapsUrl, "_blank");
            const toastRef = toast.current || parentToast?.current;
            if (toastRef) {
                toastRef.show({
                    severity: "success",
                    summary: "Navigation Started",
                    detail: `Navigating to ${stop.clientName}.`,
                    life: 4000,
                    icon: "pi pi-map-marker",
                });
            }
        }
    };

    const openMapApp = (appType) => {
        setShowMapSelectionDialog(false);
        if (!stopForDirections) return;

        const destination = `${stopForDirections.latitude},${stopForDirections.longitude}`;

        if (appType === "apple") {
            window.location.href = `maps://maps.apple.com/?daddr=${destination}&dirflg=d&t=m`;
        } else if (appType === "google") {
            window.location.href = `comgooglemaps://?daddr=${destination}&directionsmode=driving`;
        }

        const toastRef = toast.current || parentToast?.current;
        if (toastRef) {
            toastRef.show({
                severity: "success",
                summary: "Navigation Started",
                detail: `Opening ${appType === "apple" ? "Apple Maps" : "Google Maps"} for ${stopForDirections.clientName}.`,
                life: 4000,
                icon: "pi pi-map-marker",
            });
        }
    };

    const handleOpenDialog = (stop) => {
        if (stop.status === "Delivered" || stop.status === "Incomplete" || stop.status === "Skipped") {
            const toastRef = toast.current || parentToast?.current;
            if (toastRef) {
                toastRef.show({
                    severity: "info",
                    summary: "Stop Already Completed",
                    detail: `${stop.clientName}'s stop is already marked as ${stop.status.toLowerCase()}.`,
                    life: 3000,
                });
            }
            return;
        }

        // Allow re-opening skipped stops
        setSelectedStop(stop);
        setDialogVisible(true);
    };

    const handleStopUpdated = (updatedData) => {
        console.log("Received updated stop data from dialog:", updatedData);
        updateStopStatus(updatedData.stops);
        if (updatedData.stops) {
            setDeliveryStops(updatedData.stops);
        }
    };

    const handleMenuClick = (stop, event) => {
        event.preventDefault();
        event.stopPropagation();
        setSelectedStopForMenu(stop);
        const overlayPanel = overlayRefs.current[stop._id];
        if (overlayPanel) {
            overlayPanel.toggle(event);
        }
    };

    const callClient = () => {
        if (!selectedStopForMenu) return;

        const phoneNumber = selectedStopForMenu.phone || selectedStopForMenu.clientPhone || selectedStopForMenu.clientId?.phone;
        if (phoneNumber) {
            window.location.href = `tel:${phoneNumber}`;

            const toastRef = toast.current || parentToast?.current;
            if (toastRef) {
                toastRef.show({
                    severity: "success",
                    summary: "Calling Client",
                    detail: `Calling ${selectedStopForMenu.clientName}...`,
                    life: 3000,
                });
            }
        } else {
            const toastRef = toast.current || parentToast?.current;
            if (toastRef) {
                toastRef.show({
                    severity: "warn",
                    summary: "No Phone Number",
                    detail: `No phone number available for ${selectedStopForMenu.clientName}`,
                    life: 3000,
                });
            }
        }

        const overlayPanel = overlayRefs.current[selectedStopForMenu._id];
        if (overlayPanel) {
            overlayPanel.hide();
        }
    };

    const getStopStatusConfig = (status) => {
        switch (status) {
            case "Delivered":
                return {
                    icon: "pi pi-check-circle",
                    color: "#10b981",
                    bg: "#d1fae5",
                    label: "Delivered",
                    badgeClass: styles.statusDelivered,
                };
            case "Incomplete":
                return {
                    icon: "pi pi-exclamation-triangle",
                    color: "#ef4444",
                    bg: "#fee2e2",
                    label: "Incomplete",
                    badgeClass: styles.statusIncomplete,
                };
            case "Skipped":
                return {
                    icon: "pi pi-step-forward",
                    color: "#f59e0b",
                    bg: "#fef3c7",
                    label: "Skipped",
                    badgeClass: styles.statusSkipped,
                };
            case "Not Delivered":
                return {
                    icon: "pi pi-clock",
                    color: "#6366f1",
                    bg: "#ede9fe",
                    label: "Pending",
                    badgeClass: styles.statusPending,
                };
            default:
                return {
                    icon: "pi pi-clock",
                    color: "#94a3b8",
                    bg: "#f1f5f9",
                    label: "Pending",
                    badgeClass: styles.statusPending,
                };
        }
    };

    const getStopNumberColor = (index) => STOP_NUMBER_COLORS[index % STOP_NUMBER_COLORS.length];

    const deliveredCount = deliveryStops.filter((s) => s.status === "Delivered").length;
    const totalStops = deliveryStops.length;
    const completionPct = deliveryStatistics?.completionPercentage || 0;

    const completedStops = deliveryStops.filter((s) => s.status === "Delivered" || s.status === "Incomplete" || s.status === "Skipped");
    const pendingStops = deliveryStops.filter((s) => s.status === "Not Delivered");

    const renderStopCard = (stop, index) => {
        const statusConfig = getStopStatusConfig(stop.status);
        const isCompleted = stop.status === "Delivered" || stop.status === "Incomplete";
        const isSkipped = stop.status === "Skipped";
        const isNext = stop.status === "Not Delivered" && pendingStops[0]?._id === stop._id;

        return (
            <div
                key={stop._id}
                className={`${styles.stopCard} ${isCompleted ? styles.stopCardCompleted : ""} ${isSkipped ? styles.stopCardSkipped : ""}`}
                onClick={() => handleOpenDialog(stop)}
            >
                {/* Stop Number Badge */}
                <div className={styles.stopNumberBadge} style={{ background: getStopNumberColor(index) }}>
                    <span>{stop.stopNumber}</span>
                    {isNext && <div className={styles.nextIndicator}>NEXT</div>}
                </div>

                {/* Stop Info */}
                <div className={styles.stopInfo}>
                    <div className={styles.stopTopRow}>
                        <h4 className={styles.clientName}>{stop.clientName}</h4>
                        <span className={`${styles.statusBadge} ${statusConfig.badgeClass}`}>
                            <i className={statusConfig.icon} />
                            {statusConfig.label}
                        </span>
                    </div>

                    <p className={styles.contactInfo}>
                        <i className="pi pi-envelope" />
                        {stop.clientId?.email || "No email"}
                    </p>

                    <div className={styles.stopMeta}>
                        <span className={styles.metaItem}>
                            <i className="pi pi-box" />
                            {stop.packageCount || stop.packages?.length || 0} pkg
                        </span>
                        {stop.estimatedDistance && (
                            <span className={styles.metaItem}>
                                <i className="pi pi-map-marker" />
                                {stop.estimatedDistance.toFixed(1)} km
                            </span>
                        )}
                        {isCompleted && stop.completedAt && (
                            <span className={styles.metaItem}>
                                <i className="pi pi-clock" />
                                {new Date(stop.completedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </span>
                        )}
                    </div>

                    {stop.incompleteReason && (
                        <div className={styles.incompleteReason}>
                            <i className="pi pi-exclamation-triangle" />
                            <span>{stop.incompleteReason}</span>
                        </div>
                    )}

                    {stop.skipReason && (
                        <div className={styles.skipReason}>
                            <i className="pi pi-info-circle" />
                            <span>{stop.skipReason}</span>
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className={styles.stopActions}>
                    <Button icon="pi pi-directions" label="Directions" text className={styles.directionsButton} onClick={(e) => handleGetDirections(stop, e)} disabled={!stop.latitude || !stop.longitude} title={`Navigate to ${stop.clientName}`} />
                    <Button
                        icon="pi pi-ellipsis-v"
                        text
                        rounded
                        className={styles.menuButton}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleMenuClick(stop, e);
                        }}
                    />
                    {isCompleted ? (
                        <div className={styles.completedIcon} style={{ color: statusConfig.color }}>
                            <i className={statusConfig.icon} />
                        </div>
                    ) : isSkipped ? (
                        <Button
                            icon="pi pi-replay"
                            rounded
                            severity="warning"
                            className={styles.retryButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDialog(stop);
                            }}
                            tooltip="Retry delivery"
                        />
                    ) : (
                        <Button
                            icon="pi pi-arrow-right"
                            rounded
                            severity="success"
                            className={styles.arrowButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDialog(stop);
                            }}
                        />
                    )}
                </div>

                <OverlayPanel ref={(el) => (overlayRefs.current[stop._id] = el)} className={styles.stopMenuOverlay}>
                    <div className={styles.menuContent}>
                        <div className={styles.menuHeader}>
                            <div className={styles.menuHeaderIcon}>
                                <i className="pi pi-user" />
                            </div>
                            <div>
                                <h4>{stop.clientName}</h4>
                                <small>{stop.clientId?.email || "No email"}</small>
                            </div>
                        </div>

                        <div className={styles.menuActions}>
                            <Button label="Call Client" icon="pi pi-phone" text className={styles.menuActionButton} onClick={callClient} />
                        </div>
                    </div>
                </OverlayPanel>
            </div>
        );
    };

    return (
        <>
            <Toast ref={toast} />

            <div className={styles.stopListContainer}>
                {/* Progress Header */}
                <div className={styles.progressHeader}>
                    <div className={styles.progressInfo}>
                        <span className={styles.progressTitle}>Delivery Stops</span>
                        <Badge value={`${deliveredCount}/${totalStops}`} severity="success" className={styles.stopsBadge} />
                        <span className={styles.progressPercent}>{completionPct}%</span>
                    </div>
                    <ProgressBar value={completionPct} className={styles.progressBar} showValue={false} />
                </div>

                {/* Stops List */}
                <div className={styles.stopsList}>
                    {deliveryStops.length === 0 && (
                        <div className={styles.emptyState}>
                            <i className="pi pi-inbox" />
                            <h4>No stops scheduled</h4>
                            <p>Stops will appear here once assigned</p>
                        </div>
                    )}

                    {/* Pending Stops Section */}
                    {pendingStops.length > 0 && (
                        <>
                            <div className={styles.sectionHeader}>
                                <i className="pi pi-clock" />
                                <span>Pending Stops</span>
                                <span className={styles.sectionCount}>{pendingStops.length}</span>
                            </div>
                            {pendingStops.map((stop, index) => renderStopCard(stop, index))}
                        </>
                    )}

                    {/* Completed Stops Section */}
                    {completedStops.length > 0 && (
                        <>
                            <div className={`${styles.sectionHeader} ${styles.sectionHeaderCompleted}`}>
                                <i className="pi pi-check-circle" />
                                <span>Completed Stops</span>
                                <span className={styles.sectionCount}>{completedStops.length}</span>
                            </div>
                            {completedStops.map((stop, index) => renderStopCard(stop, index))}
                        </>
                    )}
                </div>
            </div>

            {/* iOS Map Selection Dialog */}
            <Dialog visible={showMapSelectionDialog} onHide={() => setShowMapSelectionDialog(false)} header="Choose Navigation App" className={styles.mapSelectionDialog} modal dismissableMask draggable={false}>
                <div className={styles.mapSelectionContent}>
                    <p className={styles.dialogMessage}>Which app would you like to use for navigation?</p>

                    <div className={styles.mapButtons}>
                        <button className={styles.mapOptionButton} onClick={() => openMapApp("apple")}>
                            <div className={styles.mapIcon}>
                                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"
                                        fill="#007AFF"
                                    />
                                </svg>
                            </div>
                            <span className={styles.mapName}>Apple Maps</span>
                            <span className={styles.mapSubtitle}>Native iOS App</span>
                        </button>

                        <button className={styles.mapOptionButton} onClick={() => openMapApp("google")}>
                            <div className={styles.mapIcon}>
                                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="#4285F4" />
                                    <path d="M12 11.5C13.38 11.5 14.5 10.38 14.5 9C14.5 7.62 13.38 6.5 12 6.5C10.62 6.5 9.5 7.62 9.5 9C9.5 10.38 10.62 11.5 12 11.5Z" fill="#EA4335" />
                                </svg>
                            </div>
                            <span className={styles.mapName}>Google Maps</span>
                            <span className={styles.mapSubtitle}>Google Maps App</span>
                        </button>
                    </div>

                    {stopForDirections && (
                        <div className={styles.destinationInfo}>
                            <i className="pi pi-map-marker" />
                            <span>
                                Destination: <strong>{stopForDirections.clientName}</strong>
                            </span>
                        </div>
                    )}
                </div>
            </Dialog>

            <DeliveryStopDialog
                visible={dialogVisible}
                onHide={() => {
                    setDialogVisible(false);
                    setSelectedStop(null);
                }}
                stop={selectedStop}
                deliveryId={deliveryId}
                onStopUpdated={handleStopUpdated}
                toast={parentToast}
            />
        </>
    );
};

export default BottomDrawer;
